"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ChevronRight, Sparkles, Zap, Shield, Code } from "lucide-react";
import dreamWorldProvider from "../DreamWorldProvider";
import { setUserGlobalData, setError } from "../store/dreamworldSlice";
import { RootState } from "../store/store";
import LoadingIndicator from "../components/ui/LoadingIndicator";

export default function RegisterPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [animate, setAnimate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAnimate(true);
    // Inject keyframes for spinner
    const styleElement = document.createElement("style");
    styleElement.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .spinner {
        animation: spin 1s linear infinite;
      }
    `;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const handleRegister = async () => {
    setLoading(true);
    setError(null);
    try {
      const userGlobalData = await dreamWorldProvider.signInWithPi();
      dispatch(setUserGlobalData(userGlobalData));
      localStorage.setItem("isSignedIn", "true");
      navigate("/home");
    } catch (err: any) {
      setError(err.message || "Failed to sign in.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen w-full bg-gradient-to-br from-gray-900 to-gray-800 font-sans text-white">
      <div className="flex w-11/12 max-w-4xl h-[80vh] max-h-[600px] rounded-2xl overflow-hidden shadow-2xl bg-white">
        {/* Brand Section */}
        <div className="hidden md:flex flex-1 flex-col justify-center p-10 bg-gradient-to-br from-indigo-600 to-purple-700">
          <div className="relative z-10 max-w-sm">
            <div className="flex items-center mb-5">
              <Sparkles size={40} className="mr-3 filter drop-shadow-lg" />
              <h1 className="text-lg font-bold tracking-tight">DreamWorld</h1>
            </div>
            <h2 className="text-2xl font-semibold leading-snug mb-4">
              Step into a new reality of social interaction.
            </h2>
            <p className="text-indigo-100 text-opacity-90 mb-8">
              Connect with your friends and the world around you through the
              power of the Pi Network.
            </p>
            <div className="space-y-4">
              <div className="flex items-center text-sm">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/20 mr-3">
                  <Zap size={18} />
                </div>
                <span className="font-medium">Instantaneous connection</span>
              </div>
              <div className="flex items-center text-sm">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/20 mr-3">
                  <Shield size={18} />
                </div>
                <span className="font-medium">Secure and private</span>
              </div>
              <div className="flex items-center text-sm">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/20 mr-3">
                  <Code size={18} />
                </div>
                <span className="font-medium">Powered by modern tech</span>
              </div>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="flex flex-1 flex-col items-center justify-center p-10 bg-gray-50 text-gray-800">
          <div
            className={`w-full max-w-xs transition-all duration-500 ease-in-out ${animate ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
          >
            <h2 className="text-lg font-bold text-center mb-2">
              Create an Account
            </h2>
            <p className="text-gray-500 text-center mb-8">
              Connect with Pi to get started
            </p>

            {error && (
              <p className="text-red-500 text-sm text-center mb-4">{error}</p>
            )}

            <button
              onClick={handleRegister}
              disabled={loading}
              className="flex items-center justify-center w-full p-4 text-base font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-700 rounded-xl shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-300 ease-in-out disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <span className="mr-2">Connect with Pi</span>
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
